import { useConfig } from '../context/ConfigContext';
import {
  PAYMENT_STATUS_PAID,
  PAYMENT_SUMMARY_TOTAL_LABEL,
  PAYMENT_SUMMARY_PAID_LABEL,
  PAYMENT_SUMMARY_BALANCE_LABEL,
} from '../constants/bookingConst';
import { formatMoneyFixed } from '../utils/currencyUtil';
import { getPaymentBreakdown, describePartialPaymentTag } from '../utils/partialPaymentUtil';

/**
 * The money box on the Track Booking card, always to two decimals
 * ("₱710.00").
 *
 * A full payment is the single "Total Paid" figure. A partial payment —
 * the guest ticked "Partial payment" at checkout, or the admin recorded a
 * downpayment — shows the total, what was paid and the remaining balance,
 * from the same getPaymentBreakdown() the email, PDF and confirmation
 * screen use.
 *
 * @param {object} props
 * @param {object} props.booking - the booking being shown; paymentAmount is the full total
 * @returns {JSX.Element}
 */
function BookingStatusPaymentBox({ booking }) {
  const { config } = useConfig();
  // Once the admin marks the balance settled the booking is paid in full,
  // even though the guest's original "partial" tick is still on the record
  // — getPaymentBreakdown would otherwise keep reporting a balance.
  const breakdown = booking.paymentStatus === PAYMENT_STATUS_PAID ? null : getPaymentBreakdown(booking, config);

  if (!breakdown) {
    return (
      <div className="info-item">
        <label>{PAYMENT_SUMMARY_PAID_LABEL}</label>
        <p className="total-amount">{formatMoneyFixed(booking.paymentAmount || 0, config)}</p>
      </div>
    );
  }

  return (
    <div className="info-item">
      <label>Payment</label>
      <div className="bss-pay-row">
        <span>{PAYMENT_SUMMARY_TOTAL_LABEL}</span>
        <span>{formatMoneyFixed(breakdown.total, config)}</span>
      </div>
      <div className="bss-pay-row bss-pay-row--paid">
        <span>
          {PAYMENT_SUMMARY_PAID_LABEL}
          <span className="bss-pay-tag">{describePartialPaymentTag(breakdown)}</span>
        </span>
        <span className="total-amount">{formatMoneyFixed(breakdown.amountPaid, config)}</span>
      </div>
      <div className="bss-pay-row bss-pay-row--balance">
        <span>{PAYMENT_SUMMARY_BALANCE_LABEL}</span>
        <span>{formatMoneyFixed(breakdown.balance, config)}</span>
      </div>
    </div>
  );
}

export default BookingStatusPaymentBox;
